import { ReactElement, Suspense, lazy, useEffect, useState } from 'react';

import styled from 'styled-components';

import { theme } from '@styles/theme';
import { getImageExtensionFromMimeType } from '@utils/assets';
import { getFileExtension } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { ImageViewer } from './image-viewer';

import { ImageExtension } from '../types/file_extension';

const PDF = lazy(() => import('./PDF'));

const TEXT_EXTENSIONS = ['txt', 'csv', 'rtf'];

export type PreviewerProps = {
  url: string | null;
  fileName?: string;
  mimeType?: string;
  paginate?: boolean;
  maxWidth: number;
  parentId: string;
  fallback: ReactElement;
};

export function Previewer({
  url,
  fileName,
  mimeType,
  paginate = false,
  maxWidth,
  parentId,
  fallback,
}: PreviewerProps) {
  const [pdfBlob, setPdfBlob] = useState<Blob | undefined>(undefined);
  const [textContent, setTextContent] = useState<string | undefined>(undefined);
  const urlExtension = getFileExtension(url)?.toLowerCase();
  const fileNameExtension = getFileExtension(fileName)?.toLowerCase();
  const mimeTypeExtension =
    mimeType && mimeType.includes('/')
      ? getImageExtensionFromMimeType(mimeType).replace('.', '').toLowerCase()
      : null;
  const isSupportedExtension = (extension: string | null | undefined) =>
    extension != null && (extension === 'pdf' || extension in ImageExtension);
  const fileExtension = isSupportedExtension(urlExtension)
    ? urlExtension
    : fileNameExtension || mimeTypeExtension;

  useEffect(() => {
    if (fileExtension === 'pdf') {
      fetch(url!)
        .then((res) => res.blob())
        .then((blob) => {
          setPdfBlob(blob);
        });
    }
    if (fileExtension && TEXT_EXTENSIONS.includes(fileExtension)) {
      fetch(url!)
        .then((res) => res.text())
        .then((text) => {
          setTextContent(text);
        });
    }
  }, [fileExtension, url]);

  if (!url || !fileExtension) {
    return fallback;
  }
  if (fileExtension === 'pdf') {
    return (
      <Suspense fallback={<div />}>
        <PDF file={pdfBlob} paginate={paginate} maxWidth={maxWidth} parentId={parentId} />
      </Suspense>
    );
  }
  if (fileExtension in ImageExtension) {
    return <ImageViewer url={url} maxWidth={maxWidth} />;
  }
  if (TEXT_EXTENSIONS.includes(fileExtension) && textContent !== undefined) {
    return <TextPreview style={{ maxWidth }}>{textContent}</TextPreview>;
  }
  return fallback;
}

const TextPreview = styled.pre({
  width: '100%',
  padding: pxToRem(24),
  background: theme.color.uiTrans5,
  borderRadius: pxToRem(8),
  color: theme.color.textPrimary,
  font: theme.font.regular14,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  overflow: 'auto',
  maxHeight: '70vh',
});

export default Previewer;
